import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import api, { useDeliveryFees } from "@/lib/api";
import { ConfirmPurchaseModal } from "@/pages/ListingDetail";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Clock, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { toast } from "sonner";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
const imgSrc = (s) => (!s ? "" : s.startsWith("http") || s.startsWith("data:") || s.startsWith("/") ? s : `data:image/jpeg;base64,${s}`);

export default function CheckoutBanner() {
  const { user } = useAuth();
  const location = useLocation();
  const feeConfig = useDeliveryFees();
  const [item, setItem] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [modalListing, setModalListing] = useState(null);
  const [opening, setOpening] = useState(false);

  const load = useCallback(async () => {
    if (!user || user.is_admin) { setItem(null); return; }
    try {
      const { data } = await api.get("/pending");
      setItem((data.purchases && data.purchases[0]) || null);
    } catch { setItem(null); }
  }, [user]);

  useEffect(() => { load(); }, [load, location.pathname]);
  useEffect(() => {
    if (!user || user.is_admin) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load, user]);

  useEffect(() => {
    if (!item?.expires_at) return;
    const tick = () => {
      const rem = Math.floor((new Date(item.expires_at).getTime() - Date.now()) / 1000);
      setSeconds(Math.max(0, rem));
      if (rem <= 0) setItem(null);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [item]);

  // Hide on the checkout page itself and when there's no active lock
  if (!item || seconds <= 0 || location.pathname.startsWith("/checkout")) return null;

  const release = async () => {
    try { await api.post(`/listings/${item.listing_id}/unlock`); } catch { /* best-effort */ }
    setItem(null);
    toast("Item released — now available to others");
  };

  const openConfirm = async () => {
    setOpening(true);
    try { const { data } = await api.get(`/listings/${item.listing_id}`); setModalListing(data); }
    catch { toast.error("Couldn't load this item — please try again"); }
    finally { setOpening(false); }
  };

  return (
    <div data-testid="checkout-banner" className="sticky top-16 z-40 border-b border-primary/20 bg-white shadow-sm">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 sm:flex-nowrap sm:px-6">
        {item.ai_thumbnail && <img src={imgSrc(item.ai_thumbnail)} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />}
        <div className="min-w-0 flex-1">
          <Link to={`/listing/${item.listing_id}`} className="block truncate text-sm font-semibold text-slate-900 hover:text-primary hover:underline" data-testid="banner-title">{item.title}</Link>
          <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-primary">
            <Clock className="h-3 w-3" /> Reserved for you — <span className="font-mono font-bold" data-testid="banner-timer">{fmt(seconds)}</span> left
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" data-testid="banner-info" aria-label="Why is this reserved?" className="ml-0.5 inline-flex text-primary/70 hover:text-primary"><Info className="h-3.5 w-3.5" /></button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-xs leading-snug" data-testid="banner-tooltip">
                  This item is held for you. If you don't complete checkout before the timer ends, it becomes available to other buyers again.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </p>
        </div>
        <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
          <Button data-testid="banner-release" variant="ghost" onClick={release} className="shrink-0 rounded-full text-sm font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700">
            Release &amp; keep browsing
          </Button>
          <Button data-testid="banner-complete" onClick={openConfirm} disabled={opening || !feeConfig} className="flex-1 shrink-0 rounded-full bg-primary font-semibold hover:bg-primary/90 sm:flex-none">
            Complete Purchase
          </Button>
        </div>
      </div>
      <ConfirmPurchaseModal open={!!modalListing} onOpenChange={(o) => { if (!o) setModalListing(null); }} listing={modalListing} user={user} feeConfig={feeConfig}
        isFree={modalListing?.listing_type === "free"} price={item.negotiation_id ? (item.agreed_price ?? undefined) : undefined} negId={item.negotiation_id || null} />
    </div>
  );
}
